import { useLocation, useParams } from "react-router-dom";
import { languageFromPath } from "@/lib/site/publicPages";
import { isLandingLocale } from "@/lib/landing/locale";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingHero } from "@/components/landing/LandingHero";
import { HowItWorksSection } from "@/components/landing/HowItWorksSection";
import { MatchingSection } from "@/components/landing/MatchingSection";
import { DocumentsBand } from "@/components/landing/DocumentsBand";
import { AiAssistantSection } from "@/components/landing/AiAssistantSection";
import { ExtrasSection } from "@/components/landing/ExtrasSection";
import { AccountantsSection } from "@/components/landing/AccountantsSection";
import { TrustSection } from "@/components/landing/TrustSection";
import { PricingSection } from "@/components/landing/PricingSection";
import { FaqSection } from "@/components/landing/FaqSection";
import { FinalCta } from "@/components/landing/FinalCta";
import { ToolsStrip } from "@/components/landing/ToolsStrip";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { CLOSING_BAND_JOINED, ContourField } from "@/components/landing/primitives";
import { cn } from "@/lib/utils";

/**
 * The public home page: Fio payment matching for Czech businesses.
 *
 * Bands alternate (plain, tinted, always-dark) and every section leads with a
 * mockup of the app; see "Public pages" in docs/design/DESIGN_SYSTEM.md.
 *
 * The language comes from the URL (`/cs`, `/en`), not from the app's language
 * context, so a prerendered page is in the right language before any script
 * runs. Nothing here touches window, document or storage during render, and
 * nothing imports the Supabase client (it reads localStorage on import), so
 * the page renders with renderToString in Node for prerendering.
 */
export default function Landing() {
  const { lang } = useParams<{ lang: string }>();
  const { pathname } = useLocation();
  // LanguageRoute redirects an unknown prefix before this renders; the root
  // (`/` on zettl.cz) has no prefix and is Czech.
  const locale = isLandingLocale(lang) ? lang : (languageFromPath(pathname) ?? "cs");

  return (
    <div
      lang={locale}
      className="min-h-screen overflow-x-clip bg-background text-foreground antialiased"
    >
      <a
        href="#obsah"
        className="sr-only z-50 rounded-md bg-background px-3 py-2 text-sm focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:px-3 focus:py-2 focus:ring-2 focus:ring-[hsl(var(--action))] dark:focus:ring-primary"
      >
        {locale === "cs" ? "Přeskočit na obsah" : "Skip to content"}
      </a>
      <LandingNav locale={locale} />
      <main id="obsah">
        <LandingHero locale={locale} />
        <HowItWorksSection locale={locale} />
        <MatchingSection locale={locale} />
        <DocumentsBand locale={locale} />
        <AiAssistantSection locale={locale} />
        <ExtrasSection locale={locale} />
        <AccountantsSection locale={locale} />
        {/* Trust before pricing: the reassurance comes before the price. */}
        <TrustSection locale={locale} />
        <PricingSection locale={locale} />
        {/* The FAQ is the last tinted band and runs on into the closing block. */}
        <FaqSection locale={locale} tone="tintedToClosing" />
      </main>
      {/* The last ask and the footer share one surface with the FAQ above: the
          wash continues from it, and contours rise from the page's bottom edge. */}
      <div className={cn("relative isolate overflow-hidden", CLOSING_BAND_JOINED)}>
        <ContourField variant="closing" />
        <FinalCta locale={locale} />
        <ToolsStrip locale={locale} />
        <LandingFooter locale={locale} />
      </div>
    </div>
  );
}
